"use client";

/**
 * Mis pedidos 4.8 / 4.9 — historial con estados y "volver a pedir".
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import { IconoCalendario } from "@/components/app/iconos";

interface PedidoResumen {
  codigo: string;
  tipo: string;
  items: { slug: string; nombre: string; qty: number; gradiente: string }[];
  total: number;
  estado: string;
  creado: string;
}

const ETIQUETA_ESTADO: Record<string, string> = {
  recibido: "Recibido",
  pago_verificado: "Pago verificado",
  preparacion: "En preparación",
  en_camino: "En camino",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

function chipEstado(estado: string): string {
  if (estado === "entregado" || estado === "cancelado")
    return "bg-humo text-tinta/60";
  return "bg-lima/30 text-bosque";
}

function fechaCorta(iso: string): string {
  const f = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  return f.toLocaleDateString("es-EC", { day: "numeric", month: "short" });
}

export function PedidosCliente() {
  const router = useRouter();
  const { usuario, agregar } = useApp();
  const [pedidos, setPedidos] = useState<PedidoResumen[] | null>(null);
  const [sinSesion, setSinSesion] = useState(false);

  useEffect(() => {
    fetch("/api/pedidos")
      .then(async (r) => {
        if (r.status === 401) {
          setSinSesion(true);
          setPedidos([]);
          return;
        }
        const data = await r.json();
        setPedidos(data.ok ? data.pedidos : []);
      })
      .catch(() => setPedidos([]));
  }, [usuario?.id]);

  function volverAPedir(p: PedidoResumen) {
    for (const item of p.items) agregar(item.slug, item.qty);
    router.push("/pedido");
  }

  return (
    <>
      <HeaderTitulo titulo="Mis pedidos" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[62px]">
        <div className="pt-[22px] pb-[30px]">
          {pedidos === null ? (
            <p className="pt-10 text-center text-sm text-tinta/50">Cargando…</p>
          ) : sinSesion ? (
            <div className="mt-[70px] flex flex-col items-center gap-3.5 text-center">
              <span className="flex h-24 w-24 items-center justify-center rounded-[32px] bg-humo">
                <IconoCalendario color="rgba(34,31,31,.3)" />
              </span>
              <h2 className="font-app text-[25px] leading-[29px] font-black">
                Ingresa para ver tus pedidos
              </h2>
              <p className="max-w-[260px] text-sm leading-[21px] text-tinta/60">
                Con tu número de WhatsApp entras en segundos.
              </p>
              <Link
                href="/acceso?next=/pedidos"
                className="mt-1.5 flex h-[58px] items-center rounded-[29px] bg-verde px-[30px] font-app text-base font-semibold text-white"
              >
                Ingresar
              </Link>
            </div>
          ) : pedidos.length === 0 ? (
            <div className="mt-[70px] flex flex-col items-center gap-3.5 text-center">
              <span className="flex h-24 w-24 items-center justify-center rounded-[32px] bg-humo">
                <IconoCalendario color="rgba(34,31,31,.3)" />
              </span>
              <h2 className="font-app text-[25px] leading-[29px] font-black">
                Sin pedidos todavía
              </h2>
              <p className="max-w-[270px] text-sm leading-[21px] text-tinta/60">
                Cuando hagas tu primer pedido lo vas a ver acá, paso por paso.
              </p>
              <Link
                href="/productos"
                className="mt-1.5 flex h-[58px] items-center rounded-[29px] bg-verde px-[30px] font-app text-base font-semibold text-white"
              >
                Hacer mi primer pedido
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {pedidos.map((p) => {
                const boots = p.items.reduce((t, i) => t + i.qty, 0);
                const activo = !["entregado", "cancelado"].includes(p.estado);
                return (
                  <div
                    key={p.codigo}
                    className="rounded-3xl border border-black/5 bg-white p-[18px]"
                  >
                    <button
                      type="button"
                      onClick={() => router.push(`/pedidos/${p.codigo}`)}
                      className="block w-full text-left"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-app text-base font-semibold">
                          {p.codigo}
                        </span>
                        <span
                          className={`rounded-[20px] px-3 py-[5px] text-[11px] font-bold ${chipEstado(p.estado)}`}
                        >
                          {ETIQUETA_ESTADO[p.estado] ?? p.estado}
                        </span>
                      </div>
                      <div className="mt-2 text-[13px] text-tinta/60">
                        {fechaCorta(p.creado)} · {boots} boots · ${p.total.toFixed(2)}
                      </div>
                      <div className="mt-3 flex gap-1.5">
                        {p.items.slice(0, 5).map((i) => (
                          <span
                            key={i.slug}
                            className="h-[34px] w-[34px] rounded-xl"
                            style={{ background: i.gradiente }}
                          />
                        ))}
                      </div>
                    </button>
                    <div className="mt-3.5 flex gap-2.5">
                      {!activo && p.estado !== "cancelado" && (
                        <button
                          type="button"
                          onClick={() => volverAPedir(p)}
                          className="flex h-10 items-center rounded-[20px] bg-bosque px-[18px] font-app text-[13px] font-semibold text-white"
                        >
                          Volver a pedir
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => router.push(`/pedidos/${p.codigo}`)}
                        className="flex h-10 items-center rounded-[20px] border border-black/10 px-[18px] font-app text-[13px] font-semibold"
                      >
                        Ver detalle
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <BottomNav />
    </>
  );
}
